import React, { useState, useEffect } from 'react';
import { api } from '@/api';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Settings, CheckCircle2, Eye, Edit3, Gavel,
  AlertTriangle, ArrowRight
} from 'lucide-react';

export default function ContractRules({ projectId, settings, onSettingsUpdate }) {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stageLabels, setStageLabels] = useState(null);
  const [selectedProfile, setSelectedProfile] = useState(null);

  useEffect(() => {
    loadProfiles();
  }, []);

  const loadProfiles = async () => {
    try {
      const res = await api.closeout.getProfiles();
      setProfiles(res.data);
    } catch { /* silent */ } finally { setLoading(false); }
  };

  const viewProfile = async (profile) => {
    setSelectedProfile(profile);
    try {
      const family = profile.contract_family;
      const res = await api.closeout.getStageLabels(family);
      setStageLabels(res.data.stages);
    } catch { /* silent */ }
  };

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin h-8 w-8 border-2 border-slate-300 border-t-slate-800 rounded-full" /></div>;

  const currentFamily = settings?.contract_family || 'JBCC';

  return (
    <div className="space-y-5" data-testid="contract-rules">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-barlow font-bold uppercase tracking-tight">
          Templates & Contract Rules
        </h2>
      </div>

      {/* Current Project Profile */}
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="p-4">
          <h3 className="text-xs font-bold uppercase text-blue-700 mb-2 flex items-center gap-1.5">
            <Gavel className="h-3.5 w-3.5" /> Active Profile for This Project
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-xs text-blue-600">Contract Family</p>
              <p className="text-sm font-bold text-blue-900">{currentFamily}</p>
            </div>
            <div>
              <p className="text-xs text-blue-600">Completion Term</p>
              <p className="text-sm font-bold text-blue-900">{settings?.profile?.completion_term || 'Completion'}</p>
            </div>
            <div>
              <p className="text-xs text-blue-600">Post-Completion</p>
              <p className="text-sm font-bold text-blue-900">{settings?.profile?.post_completion_term || 'Defects Period'}</p>
            </div>
            <div>
              <p className="text-xs text-blue-600">Final Certificate</p>
              <p className="text-sm font-bold text-blue-900">{settings?.profile?.final_term || 'Final Completion'}</p>
            </div>
          </div>
          <div className="flex items-center gap-4 mt-3">
            <Badge className={settings?.profile?.live_defects_enabled ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}>
              {settings?.profile?.live_defects_enabled ? 'Live Defects Enabled' : 'End-stage Defects Only'}
            </Badge>
            <Badge className="bg-blue-100 text-blue-700">
              {settings?.profile?.defect_label || 'Defect'} / {settings?.profile?.snag_label || 'Snag'}
            </Badge>
            {settings?.profile?.minor_defect_can_coexist_with_completion && (
              <Badge className="bg-amber-100 text-amber-700">Minor defects can coexist with completion</Badge>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Profile Detail View */}
      {selectedProfile && (
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-bold uppercase">
                {selectedProfile.name}
                {selectedProfile.is_default && <Badge className="ml-2 bg-slate-200 text-slate-600 text-[10px]">Default</Badge>}
              </h3>
              <Button variant="ghost" size="sm" onClick={() => { setSelectedProfile(null); setStageLabels(null); }}>Close</Button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
              <div>
                <p className="text-xs text-slate-500">Contract Family</p>
                <p className="text-sm font-medium">{selectedProfile.contract_family}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Edition</p>
                <p className="text-sm font-medium">{selectedProfile.edition_version || 'N/A'}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Completion Term</p>
                <p className="text-sm font-medium">{selectedProfile.completion_term}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Post-Completion</p>
                <p className="text-sm font-medium">{selectedProfile.post_completion_term}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Final Certificate</p>
                <p className="text-sm font-medium">{selectedProfile.final_term}</p>
              </div>
            </div>

            {/* Feature Flags */}
            <h4 className="text-xs font-bold uppercase text-slate-500 mb-2">Features</h4>
            <div className="flex flex-wrap gap-2 mb-4">
              {[
                { key: 'live_defects_enabled', label: 'Live Defects' },
                { key: 'sectional_handover_enabled', label: 'Sectional Handover' },
                { key: 'asset_handover_enabled', label: 'Asset Handover' },
                { key: 'consultant_uploads_enabled', label: 'Consultant Uploads' },
                { key: 'reinspection_enabled', label: 'Reinspection' },
                { key: 'minor_defect_can_coexist_with_completion', label: 'Minor Defects at Completion' },
              ].map(flag => (
                <Badge
                  key={flag.key}
                  className={selectedProfile[flag.key] ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-400'}
                >
                  {selectedProfile[flag.key] ? <CheckCircle2 className="h-3 w-3 mr-1" /> : null}
                  {flag.label}
                </Badge>
              ))}
            </div>

            {/* Stage Flow */}
            {stageLabels && (
              <>
                <h4 className="text-xs font-bold uppercase text-slate-500 mb-2">Workflow Stages</h4>
                <div className="flex items-center gap-1 flex-wrap">
                  {stageLabels.map((s, idx) => (
                    <React.Fragment key={s.key}>
                      {idx > 0 && <ArrowRight className="h-3 w-3 text-slate-300 flex-shrink-0" />}
                      <Badge variant="outline" className="text-[10px] whitespace-nowrap">{s.label}</Badge>
                    </React.Fragment>
                  ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* All Contract Profiles */}
      <h3 className="text-sm font-bold uppercase text-slate-500">Available Contract Profiles</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {profiles.map(profile => {
          const isActive = profile.contract_family === currentFamily;
          return (
            <Card
              key={profile.id}
              className={`cursor-pointer hover:shadow-md transition-shadow ${isActive ? 'border-blue-400 ring-1 ring-blue-200' : ''}`}
              onClick={() => viewProfile(profile)}
              data-testid={`profile-${profile.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Badge className="bg-slate-900 text-white text-xs">{profile.contract_family}</Badge>
                  {isActive && <Badge className="bg-blue-100 text-blue-700 text-[10px]">Active</Badge>}
                  {profile.is_default && <Badge variant="outline" className="text-[10px]">Default</Badge>}
                </div>
                <p className="text-sm font-medium text-slate-800">{profile.name}</p>
                <p className="text-xs text-slate-400 mt-0.5">{profile.edition_version || ''}</p>
                <div className="flex items-center gap-3 mt-2 text-xs text-slate-500">
                  <span>{profile.completion_term}</span>
                  <span>{profile.live_defects_enabled ? 'Live defects' : 'End-stage'}</span>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
